<template>
  <div class="change-password-page">
    <div class="page-header">
      <el-button link class="back-btn" @click="goBack">
        <el-icon><ArrowLeft /></el-icon>
        {{ $t('common.back') }}
      </el-button>
      <h1 class="page-title">{{ $t('profile.changePassword') }}</h1>
    </div>

    <el-card class="form-card" shadow="never">
      <el-form :model="passwordForm" :rules="rules" ref="formRef" class="password-form">
        <!-- Phone -->
        <el-form-item prop="phone" label="Phone">
          <el-input 
            v-model="passwordForm.phone" 
            placeholder="Please enter phone number"
            clearable
          >
            <template #prefix>
              <el-icon><Phone /></el-icon>
            </template>
          </el-input>
        </el-form-item>

        <!-- Verification Code -->
        <el-form-item prop="verificationCode" label="Verification Code">
          <div class="code-input-wrapper">
            <el-input 
              v-model="passwordForm.verificationCode" 
              placeholder="Please enter the code"
              clearable
            >
              <template #prefix>
                <el-icon><Lock /></el-icon>
              </template>
            </el-input>
            <el-button 
              class="send-code-btn" 
              type="primary" 
              @click="sendVerificationCode"
              :loading="sendingCode"
              :disabled="!passwordForm.phone || countdown > 0"
            >
              {{ countdown > 0 ? `${countdown}s` : 'Send Code' }}
            </el-button>
          </div>
        </el-form-item>

        <!-- New Password -->
        <el-form-item prop="newPassword" label="New Password">
          <el-input 
            v-model="passwordForm.newPassword" 
            type="password"
            placeholder="Enter new password (6+ characters)"
            show-password
            clearable
          >
            <template #prefix>
              <el-icon><Lock /></el-icon>
            </template>
          </el-input>
        </el-form-item>

        <!-- Confirm Password -->
        <el-form-item prop="confirmPassword" label="Confirm Password">
          <el-input 
            v-model="passwordForm.confirmPassword" 
            type="password"
            placeholder="Please confirm the new password"
            show-password
            clearable
          >
            <template #prefix>
              <el-icon><Lock /></el-icon>
            </template>
          </el-input>
        </el-form-item>

        <!-- Submit Button -->
        <div class="form-actions">
          <el-button 
            type="primary" 
            class="confirm-btn" 
            @click="submitForm"
            :loading="loading"
          >
            Confirm
          </el-button>
        </div>
      </el-form>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ArrowLeft, Phone, Lock } from '@element-plus/icons-vue'
import { changePassword } from '@/api/auth'

const router = useRouter()
const formRef = ref()
const loading = ref(false)
const sendingCode = ref(false)
const countdown = ref(0)

const passwordForm = reactive({
  phone: '',
  verificationCode: '',
  newPassword: '',
  confirmPassword: ''
})

const rules = {
  phone: [
    { required: true, message: 'Please enter phone number', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: 'Please enter valid phone number', trigger: 'blur' }
  ],
  verificationCode: [{ required: true, message: 'Please enter the code', trigger: 'blur' }],
  newPassword: [
    { required: true, message: 'Please enter new password', trigger: 'blur' },
    { min: 6, message: 'Password must be at least 6 characters', trigger: 'blur' }
  ],
  confirmPassword: [
    { required: true, message: 'Please confirm password', trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        if (value !== passwordForm.newPassword) {
          callback(new Error('Passwords do not match'))
        } else {
          callback()
        }
      },
      trigger: 'blur'
    }
  ]
}

const sendVerificationCode = async () => {
  if (!passwordForm.phone) {
    ElMessage.warning('Please enter phone number first')
    return
  }
  
  sendingCode.value = true
  try {
    // TODO: Replace with actual API call to send verification code
    // await sendSmsCode(passwordForm.phone)
    
    // Simulate sending code
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    ElMessage.success('Verification code sent')
    
    // Start countdown
    countdown.value = 60
    const timer = setInterval(() => {
      countdown.value--
      if (countdown.value <= 0) {
        clearInterval(timer)
      }
    }, 1000)
  } catch (error) {
    console.error('Failed to send code:', error)
    ElMessage.error('Failed to send verification code')
  } finally {
    sendingCode.value = false
  }
}

const submitForm = async () => {
  if (!formRef.value) return
  await formRef.value.validate(async (valid) => {
    if (!valid) return
    
    try {
      await ElMessageBox.confirm(
        'Are you sure you want to change your password?',
        'Change Password',
        {
          confirmButtonText: 'Confirm',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }
      )
      
      loading.value = true
      
      // TODO: Replace with actual API call
      // await changePassword({
      //   phone: passwordForm.phone,
      //   code: passwordForm.verificationCode,
      //   newPassword: passwordForm.newPassword
      // })
      
      // Simulate password change
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      ElMessage.success('Password changed successfully')
      router.push('/profile')
    } catch (error) {
      if (error !== 'cancel') {
        console.error('Failed to change password:', error)
        ElMessage.error('Failed to change password')
      }
    } finally {
      loading.value = false
    }
  })
}

const goBack = () => {
  router.back()
}
</script>

<style scoped lang="scss">
.change-password-page {
  min-height: 100vh;
  background-color: #f5f7fa;
  padding: 20px;

  .page-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;

    .back-btn {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 14px;
      color: #606266;
      
      &:hover {
        color: #409eff;
      }
    }

    .page-title {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      color: #303133;
    }
  }

  .form-card {
    max-width: 600px;
    margin: 0 auto;
    border-radius: 12px;

    :deep(.el-card__header) {
      padding: 0;
      border-bottom: none;
      background: transparent;
    }

    :deep(.el-card__body) {
      padding: 32px 24px;
    }

    .password-form {
      .el-form-item {
        margin-bottom: 24px;

        :deep(.el-form-item__label) {
          font-weight: 500;
          color: #606266;
        }

        :deep(.el-form-item__content) {
          margin-left: 0 !important;
        }
      }

      .code-input-wrapper {
        display: flex;
        gap: 12px;
        align-items: center;

        .el-input {
          flex: 1;
        }

        .send-code-btn {
          flex-shrink: 0;
          height: 40px;
          padding: 0 20px;
          font-size: 14px;
          
          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        }
      }

      :deep(.el-input__prefix) {
        .el-icon {
          font-size: 16px;
          color: #c0c4cc;
        }
      }

      .form-actions {
        margin-top: 32px;

        .confirm-btn {
          width: 100%;
          height: 48px;
          font-size: 16px;
          font-weight: 500;
          border-radius: 8px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
          transition: all 0.3s ease;

          &:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
          }

          &:active {
            transform: translateY(0);
          }

          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        }
      }
    }
  }
}
</style>
